import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, extractApiError } from "@/lib/api";
import { useLanguage, useT } from "@/lib/LanguageContext";
import ThemedPageLayout from "@/templates/ThemedPageLayout";
import { Loader2, CheckCircle2, XCircle, CalendarClock } from "lucide-react";
import { format, parse, startOfMonth } from "date-fns";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
// BUG-193 — relative, not "@/...": Jest has no "@" alias (craco.config.js), and
// this surface's own test (ManageBooking.rescheduleCalendar.test.js) renders the
// REAL react-day-picker grid with no mocks at all, so the whole chain down to it
// must resolve without one. public/Reserver.jsx keeps its own long-standing
// aliased import of the same calendar.jsx file — see
// components/booking/AvailabilityCalendar.jsx's own docstring for why the two
// callers may spell this differently.
import { Calendar } from "../components/ui/calendar";
import AvailabilityCalendar, { DAY_KEY_FMT } from "../components/booking/AvailabilityCalendar";

// Same formatting idiom as Reserver.jsx (public/Reserver.jsx:13-33): English is the
// FALLBACK, not French — `en` is BASE_LANG and every missing i18n key degrades to it,
// so an unknown `lang` here must not silently format like a French page.
const FMT_LOCALES = { en: "en-CA", es: "es-ES", fr: "fr-CA" };
const FMT_FALLBACK = "en-CA";

const fmtDayLong = (d, lang) =>
  new Date(d).toLocaleDateString(FMT_LOCALES[lang] || FMT_FALLBACK, {
    weekday: "long", day: "numeric", month: "long", year: "numeric",
  });

const fmtTime = (d, lang) =>
  new Date(d).toLocaleTimeString(FMT_LOCALES[lang] || FMT_FALLBACK, {
    hour: "2-digit", minute: "2-digit",
  });

// Backend refusal codes (booking_policy.py / modules/booking/schemas.py
// BookingManageView.refusal_reason, and the same codes on the two write endpoints) ->
// i18n key. Kept as a lookup table, not a template-literal key, so a code the backend
// adds later fails safe (manage.refusal.generic) instead of rendering a raw string —
// same pattern as admin/booking/calendarUtils.js ERROR_CODE_KEYS.
const REFUSAL_KEYS = {
  SELF_SERVICE_DISABLED: "manage.refusal.selfServiceDisabled",
  TOO_LATE_TO_MODIFY: "manage.refusal.tooLate",
  PAYMENT_PENDING: "manage.refusal.paymentPending",
  NEEDS_ATTENTION: "manage.refusal.needsAttention",
  BOOKING_CANCELLED: "manage.refusal.bookingCancelled",
  // F-25 — the OTHER terminal status. `BOOKING_CANCELLED` says "this was called off";
  // this says "this already happened", and a client reading one in place of the other
  // would be told their consultation never took place.
  BOOKING_COMPLETED: "manage.refusal.bookingCompleted",
  // H2-09 — money already given back: moving the appointment would hold time for a
  // client who has been repaid.
  PAYMENT_REFUNDED: "manage.refusal.paymentRefunded",
  // ── the move itself (modules/booking/service.py `_reschedule_core`) ───────────────
  // The picked start stopped being offered (another client took it, or the practice
  // closed that time) between the list being read and the submit. Remedy: another time,
  // from a fresh read — see RELOAD_STARTS_CODES.
  TIME_UNAVAILABLE: "manage.refusal.timeUnavailable",
  // A move to where the appointment already is. The list leaves the booking's own time
  // out, so only a stale page can send it; said plainly all the same.
  SAME_TIME: "manage.refusal.sameTime",
  // The service is now delivered the other way (in person ↔ video) than this booking was
  // sold as: a move cannot quietly change where the appointment happens.
  DELIVERY_MISMATCH: "manage.refusal.deliveryMismatch",
  // The booking itself moved (the practice rescheduled it) while this request was in
  // flight. Remedy: look at where it is now — see RELOAD_VIEW_CODES.
  CONFLICT: "manage.refusal.conflict",
  // The two the MONEY half of a per-minute consultation can answer through this door
  // (modules/gateway/consults.release_consult_hold, translated into this surface's bare
  // codes by service._cancel_from_booking_surface). Cancelling such a booking releases a
  // real card authorisation, so it is refused while the consultation is running and once
  // it has been charged — neither of which the generic message could explain.
  CONSULT_IN_PROGRESS: "manage.refusal.consultInProgress",
  ALREADY_COMPLETED: "manage.refusal.consultCharged",
};

// Refusals whose remedy needs fresh data, not just a sentence. A taken time is re-read
// from the server's list (the stale one still shows it); a booking that moved under the
// request is re-read whole, so the summary states where it actually is now.
const RELOAD_STARTS_CODES = new Set(["TIME_UNAVAILABLE"]);
const RELOAD_VIEW_CODES = new Set(["CONFLICT"]);

/**
 * Standalone, no-login page reached from the manage_token link mailed with every
 * booking confirmation (GET /api/bookings/manage?token=...). Lets the client cancel
 * or reschedule their own appointment, gated entirely by what the backend already
 * decided (`can_cancel` / `can_reschedule` / `refusal_reason`) — this component never
 * re-derives the policy itself.
 */
export default function ManageBooking() {
  const { lang } = useLanguage();
  const t = useT("public");
  const tc = useT("common");

  const token = useMemo(
    () => new URLSearchParams(window.location.search).get("token") || "",
    []
  );

  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [mode, setMode] = useState("summary"); // "summary" | "reschedule"
  const [cancelOpen, setCancelOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [successNote, setSuccessNote] = useState("");

  const statusLabels = useMemo(() => ({
    pending: t("portal.status.pending"),
    confirmed: t("portal.status.confirmed"),
    cancelled: t("portal.status.cancelled"),
    pending_payment: t("portal.status.pending_payment"),
    paid_needs_attention: t("portal.status.paid_needs_attention"),
    completed: t("portal.status.completed"),
  }), [t]);

  const refusalMessage = useCallback((code) => {
    if (!code) return "";
    const key = REFUSAL_KEYS[code];
    if (!key) return t("manage.refusal.generic");
    return t(key, { hours: view?.min_notice_hours });
  }, [t, view]);

  const load = useCallback(async () => {
    if (!token) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/bookings/manage", { params: { token } });
      setView(data);
      setNotFound(false);
    } catch {
      setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // ── Reschedule: the starts THIS booking may move to ─────────────────────────────
  // GET /bookings/manage/offered-starts?token=… → [{start: ISO UTC, held_by_you: false}]:
  // computed by the server for the booking's own service and its own booked length, with
  // its own time left out (so it can move by half an hour over itself). Nothing is
  // filtered here — the list is exactly where the server would accept the move.
  const [starts, setStarts] = useState([]);
  const [startsLoading, setStartsLoading] = useState(false);
  const [selectedStart, setSelectedStart] = useState(null);
  // BUG-193 — date-first picker state, the same shape public/Reserver.jsx already
  // keeps for its own booking calendar (`selectedDayKey` / `calendarMonth`).
  const [selectedDayKey, setSelectedDayKey] = useState(null);
  const [calendarMonth, setCalendarMonth] = useState(() => startOfMonth(new Date()));

  const loadStarts = useCallback(async () => {
    setStartsLoading(true);
    try {
      const { data } = await api.get("/bookings/manage/offered-starts", { params: { token } });
      setStarts(Array.isArray(data) ? data : []);
    } catch {
      setStarts([]);
    } finally {
      setStartsLoading(false);
    }
  }, [token]);

  const openReschedule = () => {
    setSelectedStart(null);
    setSelectedDayKey(null);
    setCalendarMonth(startOfMonth(new Date()));
    setActionError("");
    setMode("reschedule");
    loadStarts();
  };

  const backToSummary = () => {
    setMode("summary");
    setSelectedStart(null);
    setActionError("");
  };

  const startsByDay = useMemo(() => {
    const map = {};
    for (const s of starts) {
      const key = format(new Date(s.start), DAY_KEY_FMT);
      (map[key] = map[key] || []).push(s);
    }
    Object.values(map).forEach((arr) => arr.sort((a, b) => new Date(a.start) - new Date(b.start)));
    return map;
  }, [starts]);

  const sortedDayKeys = useMemo(() => Object.keys(startsByDay).sort(), [startsByDay]);
  const availableDays = useMemo(() => new Set(sortedDayKeys), [sortedDayKeys]);

  // BUG-193 — select the first available day (and its month), the same parity decision
  // public/Reserver.jsx makes for the booking flow's own calendar: a client rescheduling
  // should see times immediately rather than an empty panel under an otherwise-unselected
  // calendar. Also re-run when a re-read (TIME_UNAVAILABLE) emptied the selected day.
  useEffect(() => {
    if (!sortedDayKeys.length) return;
    if (selectedDayKey && availableDays.has(selectedDayKey)) return;
    const first = sortedDayKeys[0];
    setSelectedDayKey(first);
    setCalendarMonth(startOfMonth(parse(first, DAY_KEY_FMT, new Date())));
  }, [sortedDayKeys, availableDays, selectedDayKey]);

  const selectedDateObj = selectedDayKey ? parse(selectedDayKey, DAY_KEY_FMT, new Date()) : undefined;
  const todayMonthStart = startOfMonth(new Date());
  const lastMonthStart = sortedDayKeys.length
    ? startOfMonth(parse(sortedDayKeys[sortedDayKeys.length - 1], DAY_KEY_FMT, new Date()))
    : todayMonthStart;
  const endMonth = lastMonthStart < todayMonthStart ? todayMonthStart : lastMonthStart;

  const dayStarts = selectedDayKey ? (startsByDay[selectedDayKey] || []) : [];

  const onDaySelect = (key) => {
    setSelectedDayKey(key);
    setSelectedStart(null);
    setActionError("");
  };

  const doCancel = async () => {
    if (busy) return;
    setBusy(true);
    setActionError("");
    try {
      await api.post("/bookings/manage/cancel", { token });
      setCancelOpen(false);
      setSuccessNote(t("manage.cancelSuccess"));
      await load(); // re-fetch: can_cancel/can_reschedule/refusal_reason are server-derived
    } catch (e) {
      const code = e?.response?.data?.detail;
      setCancelOpen(false);
      setActionError(typeof code === "string" ? refusalMessage(code) : extractApiError(e, t("manage.refusal.generic")));
    } finally {
      setBusy(false);
    }
  };

  const submitReschedule = async () => {
    if (busy) return;
    // Judged on click, not by disabling the button: no time picked yet is content
    // validity (the RadioGroup is a required choice, exactly like a required field),
    // and the button must say so instead of just staying inert.
    if (!selectedStart) { setActionError(t("manage.rescheduleSelectRequired")); return; }
    setBusy(true);
    setActionError("");
    try {
      // `new_start` is sent back exactly as the server spelled it in the list.
      const { data } = await api.post("/bookings/manage/reschedule", { token, new_start: selectedStart });
      setView(data);
      setMode("summary");
      setSelectedStart(null);
      setSuccessNote(t("manage.rescheduleSuccess"));
    } catch (e) {
      const code = e?.response?.data?.detail;
      setActionError(typeof code === "string" ? refusalMessage(code) : extractApiError(e, t("manage.refusal.generic")));
      if (RELOAD_STARTS_CODES.has(code)) {
        setSelectedStart(null);
        loadStarts();
      } else if (RELOAD_VIEW_CODES.has(code)) {
        // Back to the summary, which now states the appointment's CURRENT time; the
        // error explains why the move did not happen.
        setMode("summary");
        setSelectedStart(null);
        await load();
      }
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <ThemedPageLayout>
        <div className="pt-40 pb-24 flex items-center justify-center text-eh-muted" data-testid="manage-loading">
          <Loader2 className="animate-spin" size={20} />
        </div>
      </ThemedPageLayout>
    );
  }

  if (notFound || !view) {
    return (
      <ThemedPageLayout>
        <section className="pt-32 pb-24 max-w-lg mx-auto px-6 text-center" data-testid="manage-not-found">
          <XCircle size={40} className="mx-auto text-eh-muted" />
          <h1 className="font-display text-3xl text-eh-ink mt-4">{t("manage.notFoundTitle")}</h1>
          <p className="text-eh-muted mt-3">{t("manage.notFoundBody")}</p>
        </section>
      </ThemedPageLayout>
    );
  }

  const restricted = !view.can_cancel || !view.can_reschedule;

  return (
    <ThemedPageLayout>
      <section className="pt-32 pb-24 max-w-2xl mx-auto px-6" data-testid="manage-booking">
        <span className="eyebrow">{t("manage.summaryHeading")}</span>
        <h1 className="font-display text-4xl md:text-5xl text-eh-ink mt-3 leading-tight">
          {view.service || t("booking.serviceFallback")}
        </h1>

        {successNote && (
          <div className="mt-6 rounded-2xl border border-eh-sage-deep/40 bg-eh-sage/10 px-5 py-4 text-sm text-eh-sage-deep flex items-center gap-2" data-testid="manage-success">
            <CheckCircle2 size={16} /> {successNote}
          </div>
        )}

        {mode === "summary" ? (
          <div className="mt-8 bg-white border border-eh-border-soft rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-2 text-eh-ink">
              <CalendarClock size={16} className="text-eh-muted" />
              <span className="font-medium capitalize">
                {view.slot_start ? fmtDayLong(view.slot_start, lang) : "—"}
              </span>
            </div>
            {view.slot_start && (
              <div className="text-sm text-eh-muted mt-1">{fmtTime(view.slot_start, lang)}</div>
            )}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span
                className="text-xs px-2.5 py-1 rounded-full bg-eh-cream text-eh-ink"
                data-testid={`manage-status-${view.status}`}
              >
                {statusLabels[view.status] || view.status}
              </span>
              <span className="text-xs text-eh-muted" data-testid="manage-email-masked">
                {t("manage.emailMasked", { email: view.email_masked })}
              </span>
            </div>

            {actionError && (
              <div className="mt-4 text-sm text-red-600" data-testid="manage-action-error">{actionError}</div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => setCancelOpen(true)}
                disabled={!view.can_cancel}
                data-testid="manage-cancel-btn"
                className="btn-ghost text-sm disabled:opacity-40 disabled:pointer-events-none"
              >
                {t("manage.cancelAction")}
              </button>
              <button
                type="button"
                onClick={openReschedule}
                disabled={!view.can_reschedule}
                data-testid="manage-reschedule-btn"
                className="btn-primary text-sm disabled:opacity-40 disabled:pointer-events-none"
              >
                {t("manage.rescheduleAction")}
              </button>
            </div>

            {restricted && view.refusal_reason && (
              <p className="mt-4 text-xs text-eh-muted" data-testid="manage-refusal-reason">
                {refusalMessage(view.refusal_reason)}
              </p>
            )}
          </div>
        ) : (
          <div className="mt-8 bg-white border border-eh-border-soft rounded-2xl p-6 md:p-8">
            <button type="button" onClick={backToSummary} className="btn-ghost text-sm mb-6">
              {tc("actions.back")}
            </button>
            <h2 className="font-display text-2xl text-eh-ink">{t("manage.rescheduleHeading")}</h2>
            <p className="text-sm text-eh-muted mt-1">
              {t("manage.rescheduleSubtitle", { service: view.service })}
            </p>

            {startsLoading ? (
              <div className="mt-6 flex items-center gap-2 text-eh-muted text-sm">
                <Loader2 className="animate-spin" size={16} /> {tc("actions.loading")}
              </div>
            ) : sortedDayKeys.length === 0 ? (
              <div className="mt-6 text-sm text-eh-muted">{t("manage.rescheduleEmpty")}</div>
            ) : (
              <>
                {/* BUG-193 — a date-selection step BEFORE the times, matching
                    the calendar public/Reserver.jsx already offers on the
                    original booking flow, instead of every future day's
                    times in one continuous scroll. */}
                <div className="mt-6 flex justify-center">
                  <AvailabilityCalendar
                    Calendar={Calendar}
                    availableDays={availableDays}
                    lang={lang}
                    selectedDate={selectedDateObj}
                    onSelectDay={onDaySelect}
                    month={calendarMonth}
                    onMonthChange={setCalendarMonth}
                    startMonth={todayMonthStart}
                    endMonth={endMonth}
                  />
                </div>

                <div className="text-xs uppercase tracking-[0.18em] text-eh-muted mt-6 mb-2 capitalize">
                  {selectedDateObj ? fmtDayLong(selectedDateObj, lang) : t("booking.pickDate")}
                </div>

                {dayStarts.length === 0 ? (
                  <div className="text-sm text-eh-muted">{t("manage.rescheduleEmpty")}</div>
                ) : (
                  <RadioGroup
                    value={selectedStart || ""}
                    onValueChange={(start) => { setSelectedStart(start); setActionError(""); }}
                    className="space-y-2"
                  >
                    {dayStarts.map((s) => (
                      <label
                        key={s.start}
                        className="flex items-center gap-3 bg-eh-cream border border-eh-border-soft hover:border-eh-ink rounded-xl px-4 py-3 cursor-pointer text-sm"
                        data-testid={`manage-time-${s.start}`}
                      >
                        <RadioGroupItem value={s.start} data-testid={`manage-time-radio-${s.start}`} />
                        {fmtTime(s.start, lang)}
                      </label>
                    ))}
                  </RadioGroup>
                )}
              </>
            )}

            {actionError && (
              <div className="mt-4 text-sm text-red-600" data-testid="manage-reschedule-error">{actionError}</div>
            )}

            <button
              type="button"
              onClick={submitReschedule}
              // Content validity (no time picked yet) is judged on click by
              // submitReschedule() itself — only the in-flight request is a structural
              // blocker here (house rule: never disable for content validity).
              disabled={busy}
              data-testid="manage-reschedule-submit"
              className="btn-primary text-sm mt-6 disabled:opacity-40 disabled:pointer-events-none"
            >
              {busy ? <Loader2 className="animate-spin" size={14} /> : null} {t("manage.rescheduleSubmit")}
            </button>
          </div>
        )}
      </section>

      <AlertDialog open={cancelOpen} onOpenChange={(open) => { if (!open) setCancelOpen(false); }}>
        <AlertDialogContent data-testid="manage-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t("manage.cancelDialogTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {view.is_paid ? t("manage.cancelDialogBodyPaid") : t("manage.cancelDialogBodyFree")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="manage-cancel-dismiss">{t("manage.cancelDialogDismiss")}</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} disabled={busy} data-testid="manage-cancel-confirm">
              {t("manage.cancelDialogConfirm")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ThemedPageLayout>
  );
}
